import React from 'react'
// import { motion } from 'framer-motion'
import { MessageCircle, Users, Crown, Calendar, BarChart3 } from 'lucide-react'
import { useSubscriptionStore } from '../stores/subscriptionStore'

const UsageDashboard: React.FC = () => {
  const {
    usageStats,
    getRemainingChatCalls,
    getRemainingCompatibilityCalls,
    isSubscriptionActive,
    FREE_TIER_LIMIT
  } = useSubscriptionStore()

  const remainingChatCalls = getRemainingChatCalls()
  const remainingCompatibilityCalls = getRemainingCompatibilityCalls()
  const isSubscribed = isSubscriptionActive()

  const getUsagePercentage = (used: number) => {
    return Math.min(100, (used / FREE_TIER_LIMIT) * 100)
  }

  const getProgressColor = (percentage: number) => {
    if (percentage < 50) return 'from-green-500 to-emerald-500'
    if (percentage < 80) return 'from-yellow-500 to-orange-500'
    return 'from-red-500 to-pink-500'
  }

  return (
    <div className="bg-gray-800 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">Usage Dashboard</h3>
          <p className="text-gray-400 text-sm">
            {isSubscribed ? 'Unlimited access with subscription' : 'Free tier usage tracking'}
          </p>
        </div>
      </div>

      {isSubscribed ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h4 className="text-lg font-semibold text-white mb-2">Unlimited Access</h4>
          <p className="text-gray-400 text-sm">
            You have unlimited access to all features with your active subscription.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Chat Usage */}
          <div className="bg-gray-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-400" />
                <span className="text-white font-semibold">Chat with AI</span>
              </div>
              <span className="text-gray-300 text-sm">
                {usageStats.chatCalls} / {FREE_TIER_LIMIT}
              </span>
            </div>
            
            <div className="mb-2">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Used</span>
                <span>{remainingChatCalls} remaining</span>
              </div>
              <div className="bg-gray-600 rounded-full h-2">
                <div 
                  className={`bg-gradient-to-r ${getProgressColor(getUsagePercentage(usageStats.chatCalls))} h-2 rounded-full transition-all duration-300`}
                  style={{ width: `${getUsagePercentage(usageStats.chatCalls)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Compatibility Usage */}
          <div className="bg-gray-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-400" />
                <span className="text-white font-semibold">Compatibility Checks</span>
              </div>
              <span className="text-gray-300 text-sm">
                {usageStats.compatibilityCalls} / {FREE_TIER_LIMIT}
              </span>
            </div>
            
            <div className="mb-2">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Used</span>
                <span>{remainingCompatibilityCalls} remaining</span>
              </div>
              <div className="bg-gray-600 rounded-full h-2">
                <div 
                  className={`bg-gradient-to-r ${getProgressColor(getUsagePercentage(usageStats.compatibilityCalls))} h-2 rounded-full transition-all duration-300`}
                  style={{ width: `${getUsagePercentage(usageStats.compatibilityCalls)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Usage Summary */}
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-purple-400" />
              <span className="text-white font-semibold text-sm">Usage Summary</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Total Used:</span>
                <div className="text-white font-semibold">
                  {usageStats.chatCalls + usageStats.compatibilityCalls} calls
                </div>
              </div>
              <div>
                <span className="text-gray-400">Total Remaining:</span>
                <div className="text-white font-semibold">
                  {remainingChatCalls + remainingCompatibilityCalls} calls
                </div>
              </div>
            </div>
          </div>

          {/* Upgrade CTA */}
          {(remainingChatCalls === 0 || remainingCompatibilityCalls === 0) && (
            <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-xl p-4 text-center">
              <h4 className="text-white font-semibold mb-2">Upgrade for Unlimited Access</h4>
              <p className="text-gray-300 text-sm mb-3">
                You've reached the free tier limit. Upgrade to continue using all features unlimited times.
              </p>
              <button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold text-sm">
                View Plans
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default UsageDashboard 